"use client";
import React, { useEffect, useRef, useState, useMemo } from "react";
import Image from "next/image";
import { getSafeImageUrl } from "@/utils/images";
import { apiFetch } from "@/utils/api";
import { ChevronLeftIcon, ChevronRightIcon } from "@/utils/icons";

interface BannerItem {
  image: string;
  alt: string;
  href: string;
  HTMLcode?: string;
}

interface CrousalBanner {
  _id?: string;
  media?: string;
  alt?: string;
  href?: string;
  order: number;
  isActive: boolean;
  isFeatured: boolean;
  HTMLcode?: string;
  media_link?: string;
}

interface CarouselProps {
  orientation?: "horizontal" | "vertical";
}

const Carousel: React.FC<CarouselProps> = ({ orientation = "horizontal" }) => {
  const containerRef = useRef<HTMLDivElement | null>(null); 
  const [originalBanners, setOriginalBanners] = useState<BannerItem[]>([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0); 
  const [visibleSlides, setVisibleSlides] = useState(
    orientation === "vertical" ? 1 : 2
  );
  const [imgErrorMap, setImgErrorMap] = useState<{ [key: number]: boolean }>(
    {}
  );
  
  // Using 'auto' for reliable instant scroll reset
  const [scrollBehavior, setScrollBehavior] = useState<'smooth' | 'auto'>('smooth'); 
  
  const offset = visibleSlides;
  const actualLength = originalBanners.length;
  const fullLength = (actualLength > 0) ? actualLength + (2 * offset) : 0;
  const startIndex = offset; 

  // --- Data Loading ---
  useEffect(() => {
    setLoading(true);
    const url = orientation
      ? `/crousal-banners/?orientation=${orientation}&isActive=true&isFeatured=false`
      : "/crousal-banners/?isActive=true&isFeatured=false";
    apiFetch<{ success: boolean; data: CrousalBanner[] }>(url)
      .then((res) => {
        const bannersArr: CrousalBanner[] = Array.isArray(res.data)
          ? res.data
          : [];
        if (bannersArr.length > 0) {
          setOriginalBanners(
            bannersArr.map((b) => ({
              image:
                b.media_link && b.media_link.trim() !== ""
                  ? b.media_link
                  : b.media || "/assets/OfferCodeFlight.jpg",
              alt: b.alt || "",
              href: b.href || "#",
              HTMLcode: b.HTMLcode,
            }))
          );
          setError(null);
          setCurrentIndex(offset);
        } else {
          setOriginalBanners([]);
          setError("No banners found.");
          setCurrentIndex(0);
        }
      })
      .catch((err: unknown) => {
        setError((err as Error).message || "Failed to load banners");
        setOriginalBanners([]);
      })
      .finally(() => setLoading(false));
  }, [orientation, offset]);

  useEffect(() => {
    if (orientation === "vertical") {
      setVisibleSlides(1);
      return;
    }
    const handleResize = () => {
      // Assuming desktop is >= 768px for 2 slides, mobile is < 768px for 1 slide
      setVisibleSlides(window.innerWidth < 768 ? 1 : 2);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [orientation]);

  // Data Duplication for Infinite Loop
  const renderBanners = useMemo(() => {
    if (actualLength === 0) return [];
    if (orientation === "vertical") return originalBanners; 
    const prependedPart = originalBanners.slice(-offset);
    const appendedPart = originalBanners.slice(0, offset);
    
    // Result: [LAST N] + [ORIGINAL BANNERS] + [FIRST N]
    return [...prependedPart, ...originalBanners, ...appendedPart];
  }, [originalBanners, offset, actualLength, orientation]);

  // Auto-scroll (Left-to-Right only)
  useEffect(() => {
    if (orientation === "vertical" || actualLength === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        // FIX: Allow scroll to move into the appended duplicates (e.g., index 3 -> 4) 
        // We handle the instant reset in the dedicated boundary effect below.
        return prev + 1;
      }); 
    }, 3000);
    
    return () => clearInterval(interval);
  }, [actualLength, orientation]);

  // Boundary Reset Logic (The Core Loop) - Handles the instant jump
  useEffect(() => {
    if (actualLength === 0 || orientation === "vertical") return;

    let resetTo = -1;
    // If currentIndex hits this (e.g., 5 in an array of 5 indices 0-4), reset.
    if (currentIndex >= actualLength + startIndex) {
        // Jump back to the index of the original first banner (startIndex)
        resetTo = startIndex; 
    } 
    // Check if we have scrolled backward past the first original banner
    else if (currentIndex < startIndex) {
        // Jump forward to the index of the duplicated last banner
        resetTo = actualLength + startIndex - 1; 
    }
    
    if (resetTo !== -1) {
        setScrollBehavior('auto'); 
        setCurrentIndex(resetTo);
    }
    
  }, [currentIndex, actualLength, startIndex, orientation]);
  
  // Scroll Position Update
  useEffect(() => {
    const container = containerRef.current;
    if (container && fullLength > 0) {
      const slideSize = container.offsetWidth / visibleSlides;
      const scrollValue = slideSize * currentIndex;
      
      container.scrollTo({
        left: scrollValue,
        behavior: scrollBehavior, 
      });
      
      // Reset back to smooth after the instantaneous scroll
      if (scrollBehavior === 'auto') { 
        const timer = setTimeout(() => setScrollBehavior('smooth'), 100); 
        return () => clearTimeout(timer);
      }
    }
  }, [currentIndex, fullLength, visibleSlides, scrollBehavior]);


  const handleScroll = (dir: "left" | "right") => {
    setCurrentIndex((prev) => {
        const nextIndex = (dir === "left" ? prev - 1 : prev + 1);
        
        // Manual control of the loop for buttons.
        // The reset logic relies on the boundary check useEffect to apply the 'auto' scroll jump.
        return nextIndex;
    });
  };
  
  // Dot click maps to every single banner index
  const handleDotClick = (index: number) => {
    setCurrentIndex(index + offset);
  }

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center text-gray-400">
        Loading banners...
      </div>
    );
  }
  if (error || actualLength === 0) {
    return (
      <div className="w-full h-64 flex items-center justify-center text-red-500">
        {error || "No banners found."}
      </div>
    );
  }
  
  // totalDots is always actualLength for 1:1 banner mapping (mobile dots)
  const totalDots = orientation === "vertical" 
    ? actualLength 
    : actualLength; 
  
  // activeDotIndex maps the current full index back to the single original banner index
  const activeDotIndex = orientation === "vertical" 
    ? currentIndex 
    : ((currentIndex - offset) % actualLength + actualLength) % actualLength; 

  const mobileGapClass = 'px-1';
  const containerGapClass = '-mx-1'; 

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <div className="w-full relative">
        {/* Left Arrow (Visible only on Desktop) */}
        <button
          onClick={() => handleScroll("left")}
          className="absolute left-0 top-1/2 z-10 -translate-y-1/2 bg-white rounded-full p-1 shadow hover:scale-110 cursor-pointer hover:opacity-100 opacity-80 transition hidden md:block" 
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="h-4.5 w-4.5" />
        </button>
        {/* Right Arrow (Visible only on Desktop) */}
        <button
          onClick={() => handleScroll("right")}
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 bg-white rounded-full p-1 shadow hover:scale-110 cursor-pointer hover:opacity-100 opacity-80 transition hidden md:block"
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="h-4.5 w-4.5" />
        </button>
        {/* Carousel Container */}
        <div
          ref={containerRef}
          className={`flex overflow-x-hidden scrollbar-hide hide-vertical-scrollbar ${containerGapClass}`} 
        >
          {renderBanners.map((item, index) => {
            const originalIndex = (index - offset + actualLength) % actualLength;
            
            return (
              <div
                key={index} 
                className={`flex-shrink-0 w-full ${mobileGapClass} ${ 
                  orientation === "vertical" 
                    ? "w-full"
                    : visibleSlides === 2 
                      ? "md:w-1/2" 
                      : "w-full"
                }`}
              >
                {item.HTMLcode && item.HTMLcode.trim() !== '' ? (
                  // Render HTML code if available
                  <div
                    className={`relative w-full p-0 ${
                      orientation === "vertical" 
                        ? "h-[360px]" 
                        : "h-[150px] lg:h-[240px]" 
                    } rounded-md overflow-hidden hover:shadow-md hover:scale-95 transition-transform`}
                    dangerouslySetInnerHTML={{ __html: item.HTMLcode }}
                  />
                ) : (
                  // Render image if no HTML code
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    <div
                      className={`relative w-full p-0 ${
                        orientation === "vertical" 
                          ? "h-[360px]" 
                          : "h-[150px] lg:h-[240px]" 
                      } rounded-md overflow-hidden hover:shadow-md hover:scale-95 transition-transform`}
                    >
                      <Image
                        src={
                          imgErrorMap[originalIndex]
                            ? "/assets/OfferCodeFlight.jpg"
                            : getSafeImageUrl(item.image)
                        }
                        alt={item.alt}
                        fill
                        priority
                        onError={() =>
                          setImgErrorMap((map) => ({ ...map, [originalIndex]: true }))
                        }
                      />
                    </div>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Pagination Dots (Visible only on Mobile) */}
      {orientation === "horizontal" && actualLength > 1 && (
        <div className="flex justify-center space-x-2 mt-4 md:hidden">
          {Array.from({ length: totalDots }).map((_, dotIndex) => (
            <button
              key={dotIndex}
              onClick={() => handleDotClick(dotIndex)}
              className={`h-2 rounded-full transition-all duration-300 ${
                dotIndex === activeDotIndex 
                  ? 'w-6 bg-primary-600' 
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${dotIndex + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;